import React from 'react';
import './RangeChart.scss';
import {
  Biomarker,
  AppScenariosBiomarkerGetMemberResultsApplicationPayloadBiomarkerDTO,
  AppScenariosBiomarkerGetResultsByAppointmentApplicationPayloadBiomarkerDTO,
  GenderRangeData
} from '../../../../types/apiGeneratedTypes';
import {
  getAgeFromDOB,
  getClosestAgeRangeEntry,
  isAgeDependentRange,
  parseNumericValue
} from '../../../../utils/biomarkers';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store/store';

interface RangeSummaryProps {
  biomarker:
    | Biomarker
    | AppScenariosBiomarkerGetMemberResultsApplicationPayloadBiomarkerDTO
    | AppScenariosBiomarkerGetResultsByAppointmentApplicationPayloadBiomarkerDTO;
  value: number | string;
  scale: GenderRangeData | null;
}

const RangeSummary: React.FC<RangeSummaryProps> = ({
  value,
  scale,
  biomarker
}) => {
  const { memberData } = useSelector((state: RootState) => state.member);
  if (biomarker.biomarker_type === 'UNITS' || !scale) {
    return null;
  }

  const parsedValue =
    biomarker.biomarker_type === 'BINARY' || biomarker.name.toLowerCase().includes('anti-nuclear') 
      ? value 
      : parseNumericValue(value);
  if (!parsedValue) return null;

  const getValueStatus = () => {
    if (biomarker.biomarker_type === 'BINARY' || biomarker.name.toLowerCase().includes('anti-nuclear')) {
      return parsedValue === 'POSITIVE' ? 'Positive' : 'Negative';
    }
    if (!scale) return null;
    const parsed = Number(parsedValue);

    let rangeData = null;

    if (isAgeDependentRange(scale)) {
      const age = getAgeFromDOB(memberData?.dob!);
      rangeData = getClosestAgeRangeEntry(scale, age);
    } else {
      rangeData = scale;
    }

    if (!isNaN(parsed)) {
      if (typeof rangeData?.low_from === 'number' && parsed < rangeData.low_from) {
        return 'Low';
      }

      if (
        typeof rangeData?.low_from === 'number' &&
        typeof rangeData?.low_to === 'number' &&
        parsed >= rangeData.low_from &&
        parsed <= rangeData.low_to
      ) {
        return 'Low';
      }

      if (
        typeof rangeData?.below_optimal_from === 'number' &&
        parsed >= rangeData.below_optimal_from
      ) {
        // If optimal_from is missing, cap "Below optimal" at the start of the "High" range (if present)
        // so very high values are not incorrectly classified as "Below optimal".
        const highFromForCap =
          typeof rangeData?.high_from === 'number'
            ? rangeData.high_from
            : Infinity;
        const optimalFrom =
          typeof rangeData?.optimal_from === 'number'
            ? rangeData.optimal_from
            : highFromForCap;

        if (parsed < optimalFrom) {
          return 'Below optimal';
        }
      }

      if (
        typeof rangeData?.optimal_from === 'number' &&
        typeof rangeData?.optimal_to === 'number' &&
        parsed >= rangeData.optimal_from &&
        parsed <= rangeData.optimal_to
      ) {
        return 'Optimal';
      }

      // Check High range first (before Above optimal)
      if (typeof rangeData?.high_from === 'number' && parsed >= rangeData.high_from) {
        if (typeof rangeData?.high_to === 'number') {
          if (parsed <= rangeData.high_to) {
            return 'High';
          }
        } else {
          return 'High';
        }
      }

      // Check if value is WITHIN the above-optimal range (must be between from and to)
      if (
        typeof rangeData?.above_optimal_from === 'number' &&
        typeof rangeData?.above_optimal_to === 'number' &&
        parsed >= rangeData.above_optimal_from &&
        parsed <= rangeData.above_optimal_to
      ) {
        // Only return Above optimal if it's not in the High range (already checked above)
        return 'Above optimal';
      }

      // Check if value is below optimal range (even if Low/Below optimal ranges are disabled)
      if (typeof rangeData?.optimal_from === 'number' && parsed < rangeData.optimal_from) {
        // If Below optimal is enabled, use it; otherwise use Low
        if (
          typeof rangeData?.below_optimal_from === 'number' &&
          parsed >= rangeData.below_optimal_from
        ) {
          return 'Below optimal';
        }
        return 'Low';
      }

      // Check if value is above optimal range but not in High or Above optimal
      // This is a fallback for values that are above optimal_to but don't fit in any defined range
      if (typeof rangeData?.optimal_to === 'number' && parsed > rangeData.optimal_to) {
        // Only return High if High range is actually defined
        if (typeof rangeData?.high_from === 'number' && parsed >= rangeData.high_from) {
          return 'High';
        }
        // Don't return "Above optimal" here - if value is beyond above_optimal_to, it's not "Above optimal"
        // Return null to show no status (N/A)
        return null;
      }

      // Default fallback - should rarely reach here
      return null;
    }
  };

  const status = getValueStatus();
  
  // Don't render anything if there's no status (value is outside all ranges)
  if (!status) {
    return null;
  }

  const getColor = () => {
    if (biomarker.biomarker_type === 'BINARY' || biomarker.name.toLowerCase().includes('anti-nuclear')) {
      if (biomarker.value_meaning === 'POSITIVE_NORMAL') {
        return parsedValue === 'POSITIVE' ? '#fbecb0' : '#fdc8b0';
      } else {
        return parsedValue === 'POSITIVE' ? '#fdc8b0' : '#fbecb0';
      }
    }
    if (status === 'Low') return '#fdc8b0';
    if (status === 'Below optimal') return '#fbecb1';
    if (status === 'Above optimal') return '#fbecb1';
    if (status === 'High') return '#fdc8b0';
    return '#cbebce';
  };

  return (
    <div
      className='range-summary'
      style={{
        backgroundColor: getColor()
      }}
    >
      {status}
    </div>
  );
};

export default RangeSummary;
